import { apiClient } from './api-client';
import type {
  AutoCompleteResponse,
  CombinedRequest,
  DailyPriceResponse,
  DetailResponse,
  FuelPriceInfo,
  PagedModelResponse,
  PriceHistoryItem,
  RegionPriceResponse,
  Response,
  StatsResponse
} from './api/data-contracts';

// Transform API response to UI interface
export interface PetroleumStation {
  id: string;
  name: string;
  /** 표시용 대표 주소 - 도로명이 있으면 도로명 */
  address: string;
  /** 도로명주소 */
  roadAddress?: string;
  /** 지번주소 */
  jibunAddress?: string;
  latitude: number | null;
  longitude: number | null;
  phone?: string;
  fuelTypes: string[];
  brand: string;
  operatingHours: string;
  priceGasoline?: number;
  priceDiesel?: number;
  pricePremiumGas?: number;
  priceKerosene?: number;
  priceLpg?: number;
  priceUpdatedAt?: string;
  rating?: number;
  amenities: string[];
  isOpen: boolean;
  distance?: number;
  isSelf?: boolean;
  isLpg?: boolean;
  stationType?: string;
  pollCode?: string;
  priceHistory?: PriceHistoryItem[];
  // 새로 추가된 필드
  thumbnailUrl?: string;       // 썸네일 이미지 URL
  businessHours?: string;      // 영업시간 (예: 09:00 - 22:00)
  is24Hours?: boolean;         // 24시간 영업 여부
  storeCd?: string;            // 연결된 매장 코드
  hasCarWash?: boolean;        // 세차장 보유
  hasConvenienceStore?: boolean; // 편의점 보유
  hasMaintenance?: boolean;    // 정비소 보유
  hasRestroom?: boolean;       // 화장실 보유
  lastCrawledAt?: string;      // 마지막 크롤링 시간
  bizno?: string;              // 사업자등록번호
}

// 좌표가 있는 주유소 (지도 표시 가능)
export type LocatedStation = PetroleumStation & { latitude: number; longitude: number };

// 좌표 보유 여부 판별 (좌표가 없으면 지도에 표시하지 않고 목록에만 노출)
export function hasLocation(station: PetroleumStation): station is LocatedStation {
  return station.latitude !== null && station.longitude !== null;
}

// Transform Response to PetroleumStation
export function transformResponse(response: Response): PetroleumStation {
  const fuelPrice = response.fuelPrice;

  return {
    id: response.id || '',
    name: response.name || '주유소',
    address: response.roadAddress || response.jibunAddress || '',
    latitude: response.latitude ?? null,
    longitude: response.longitude ?? null,
    phone: '정보 없음',
    fuelTypes: extractFuelTypes(fuelPrice),
    brand: extractBrandFromName(response.name || ''),
    operatingHours: response.businessHours || '정보 없음',
    priceGasoline: fuelPrice?.gas,
    priceDiesel: fuelPrice?.diesel,
    pricePremiumGas: fuelPrice?.premiumGas,
    priceKerosene: fuelPrice?.kerosene,
    priceLpg: fuelPrice?.lpg,
    priceUpdatedAt: fuelPrice?.priceUpdatedAt,
    rating: response.rating,
    amenities: [],
    isOpen: true,
    distance: response.distance,
    isSelf: response.isSelf,
    isLpg: response.isLpg,
    stationType: response.stationType,
    pollCode: response.pollCode,
    // 새로 추가된 필드
    thumbnailUrl: response.thumbnailUrl,
    businessHours: response.businessHours,
    is24Hours: response.is24Hours,
    hasCarWash: response.hasCarWash,
    hasConvenienceStore: response.hasConvenienceStore,
    hasMaintenance: response.hasMaintenance,
    hasRestroom: response.hasRestroom,
    lastCrawledAt: response.lastCrawledAt,
    bizno: response.bizno
  };
}

// Helper functions
function extractBrandFromName(name: string): string {
  if (name.includes('SK')) return 'SK에너지';
  if (name.includes('GS') || name.includes('칼텍스')) return 'GS칼텍스';
  if (name.includes('현대')) return '현대오일뱅크';
  if (name.includes('S-OIL')) return 'S-OIL';
  if (name.includes('알뜰')) return '알뜰주유소';
  if (name.includes('WJPNC')) return 'WJPNC';
  return '독립계';
}

function extractFuelTypes(fuelPrice?: FuelPriceInfo): string[] {
  if (!fuelPrice) return [];
  
  const types: string[] = [];
  if (fuelPrice.premiumGas) types.push('고급휘발유');
  if (fuelPrice.gas) types.push('휘발유');
  if (fuelPrice.diesel) types.push('경유');
  if (fuelPrice.kerosene) types.push('등유');
  if (fuelPrice.lpg) types.push('LPG');
  
  return types;
}

// API service functions
export class FuelStationApiService {
  // Text-based search
  static async searchByText(query: string): Promise<PetroleumStation[]> {
    try {
      const response = await apiClient.get('fuel-stations/search', {
        searchParams: { query }
      }).json<Response[]>();
      
      return response.map(transformResponse);
    } catch (error) {
      console.error('Failed to search fuel stations by text:', error);
      throw error;
    }
  }

  // Region-based search (sido + sigungu)
  static async searchByRegion(sido: string, sigungu: string): Promise<PetroleumStation[]> {
    try {
      const response = await apiClient.get('fuel-stations/search/region', {
        searchParams: { sido, sigungu }
      }).json<Response[]>();

      return response.map(transformResponse);
    } catch (error) {
      console.error('Failed to search fuel stations by region:', error);
      throw error;
    }
  }

  // Find nearest stations
  static async findNearestStations(
    latitude: number,
    longitude: number,
    limit: number = 10
  ): Promise<PetroleumStation[]> {
    try {
      const response = await apiClient.get('fuel-stations/search/nearest', {
        searchParams: {
          latitude: latitude.toString(),
          longitude: longitude.toString(),
          limit: limit.toString()
        }
      }).json<Response[]>();

      return response.map(transformResponse);
    } catch (error) {
      console.error('Failed to find nearest fuel stations:', error);
      throw error;
    }
  }

  // Get station detail with price history
  static async getStationDetail(id: string, days: number = 7): Promise<PetroleumStation | null> {
    try {
      const response = await apiClient.get(`fuel-stations/search/${id}`, {
        searchParams: {
          days: days.toString()
        }
      }).json<DetailResponse>();
      const fuelPrice = response.fuelPrice;

      // Transform DetailResponse to PetroleumStation
      return {
        id: response.id || '',
        name: response.name || '주유소',
        address: response.roadAddress || response.jibunAddress || '',
        roadAddress: response.roadAddress,
        jibunAddress: response.jibunAddress,
        latitude: response.latitude ?? null,
        longitude: response.longitude ?? null,
        phone: '정보 없음',
        fuelTypes: extractFuelTypes(fuelPrice),
        brand: extractBrandFromName(response.name || ''),
        operatingHours: response.businessHours || '정보 없음',
        priceGasoline: fuelPrice?.gas,
        priceDiesel: fuelPrice?.diesel,
        pricePremiumGas: fuelPrice?.premiumGas,
        priceKerosene: fuelPrice?.kerosene,
        priceLpg: fuelPrice?.lpg,
        priceUpdatedAt: fuelPrice?.priceUpdatedAt,
        rating: response.rating,
        amenities: [],
        isOpen: true,
        isSelf: response.isSelf,
        isLpg: response.isLpg,
        stationType: response.stationType,
        pollCode: response.pollCode,
        priceHistory: response.priceHistory,
        // 새로 추가된 필드
        thumbnailUrl: response.thumbnailUrl,
        businessHours: response.businessHours,
        is24Hours: response.is24Hours,
        storeCd: response.storeCd,
        hasCarWash: response.hasCarWash,
        hasConvenienceStore: response.hasConvenienceStore,
        hasMaintenance: response.hasMaintenance,
        hasRestroom: response.hasRestroom,
        lastCrawledAt: response.lastCrawledAt,
        bizno: response.bizno
      };
    } catch (error) {
      console.error('Failed to get fuel station detail:', error);
      return null;
    }
  }

  // Auto-complete search
  static async autoComplete(query: string): Promise<AutoCompleteResponse[]> {
    try {
      const response = await apiClient.get('fuel-stations/search/autocomplete', {
        searchParams: { query }
      }).json<AutoCompleteResponse[]>();
      
      return response;
    } catch (error) {
      console.error('Failed to get autocomplete suggestions:', error);
      return [];
    }
  }

  // 텍스트 + 위치 복합 검색 (반경 내에서 유사도순)
  static async searchCombined(
    searchText: string,
    latitude: number,
    longitude: number,
    radiusKm: number,
    size: number = 50
  ): Promise<PetroleumStation[]> {
    try {
      const body: CombinedRequest = { searchText, latitude, longitude, radiusKm };
      const response = await apiClient.post('fuel-stations/search/combined', {
        json: body,
        searchParams: { page: '0', size: size.toString() }
      }).json<PagedModelResponse>();

      return (response.content || []).map(transformResponse);
    } catch (error) {
      console.error('Failed to run combined fuel station search:', error);
      throw error;
    }
  }

  // 시군구별 유가 평균·최저 (가격 히트맵)
  static async getRegionPrices(): Promise<RegionPriceResponse[]> {
    return apiClient.get('fuel-stations/search/region-prices').json<RegionPriceResponse[]>();
  }

  // 여러 주유소의 최근 n일 일별 유가 (타임라인)
  static async getDailyPrices(ids: string[], days: number): Promise<DailyPriceResponse[]> {
    if (ids.length === 0) return [];
    return apiClient.get('fuel-stations/search/price-histories', {
      searchParams: { ids: ids.join(','), days: days.toString() }
    }).json<DailyPriceResponse[]>();
  }

  // Get search statistics
  static async getSearchStats(): Promise<StatsResponse> {
    try {
      const response = await apiClient.get('fuel-stations/search/stats').json<StatsResponse>();
      return response;
    } catch (error) {
      console.error('Failed to get search stats:', error);
      throw error;
    }
  }
}
